import React, { useEffect, useState } from 'react';
import { STAFF_ROLE_LABELS } from '../../constants/staff';
import { formatDeduction } from '../../utils/money';
import { CASH_TAB_COPY } from './CashCollectionsTab';
import './CashCollections.css';

// Discounts/Refunds are stored as magnitudes (>=0, §API CONTRACT) — the
// table always shows them as a deduction, minus-prefixed (a zero deduction
// shows no sign, `formatDeduction`), same convention as the P2 mockup's
// waterfall columns.
const ded = formatDeduction;

/**
 * CashMemberTable (DOCTOR-REPORTS P2, FE-2) — the shared OPD/OTC "by
 * doctor" / "by staff" table (build-plan.md FE-2). One component, driven
 * entirely by props built from `monthlyReportModel.selectCashSection` — it
 * never reads the wire shape itself, never sums a total (the footer is
 * whatever `footerTerms` the caller passes, straight off the server block
 * or the one selected member's own bucket).
 *
 * - ALL rows render — this is a money report, never truncated (same rule
 *   as `ByDoctorTable`); a capped, sticky-header scroll box + a search box
 *   handle a busy month instead.
 * - Names wrap at word boundaries; a single no-space token (e.g. a very
 *   long username) still wraps rather than overflowing or ellipsising
 *   (`CashCollections.css` `.cmt-name-txt`, `overflow-wrap: break-word` —
 *   NOT `anywhere`/`break-all`, which pushed the table past its box, D2).
 * - A negative `netPaise` row renders the minus sign (already baked into
 *   `formatPaise`) in a rose tint plus a stated tag — never just a colour
 *   change a colour-blind reader could miss.
 * - `showRole` renders a role subline under the name (OTC only, per the
 *   mockup) instead of a separate column — kept off OPD, which is always
 *   doctors.
 * - `showKindColumns` (default `true`) hides the Consultation / Procedures /
 *   Referral header, body and footer cells — lab has no kind split (the 3
 *   terms are a documented constant 0 on the wire, §API CONTRACT), so the
 *   lab-by-technician table passes `false` rather than render 3 all-zero
 *   columns.
 * - Row click calls `onRowSelect` ONLY when `isRowClickable(row)` is true;
 *   clicking the already-selected row clears the filter (P1 pattern,
 *   `ByDoctorTable`/`DoctorPicker`).
 * - `resetKey` (D3, bk-1522): the search box is local state, keyed to the
 *   period — when the caller passes a new value (the board's `period.month`)
 *   the search clears, same as the doctor filter reset on month change. No
 *   extra fetch — this is local state only.
 * - Net collected (the payout column) is sticky on the right (`.cmt-col-sticky`,
 *   CashCollections.css) so it stays visible without side-scrolling at 1280
 *   even when the middle money columns run wide (P2 QA tuning) — the name
 *   column's own behaviour (scrolls with the body) is unchanged.
 * - R1-1 (re-QA round 2): the "Refunds exceeded…" tag and the footer label
 *   are NOT `whitespace-nowrap` — in a `table-layout:auto` table an
 *   unbreakable string forces the WHOLE column to at least that width
 *   (~220px for the tag), which is what pushed the table past its box even
 *   on ordinary values. Letting them wrap (still never truncated/ellipsised)
 *   plus tighter money-column padding (`px-2`) and `tabular-nums` keeps the
 *   table inside its box at 1280/1440 for realistic values; `break-word` on
 *   the name (NOT `anywhere` — that splits ordinary words, the P1 lesson)
 *   still stands.
 */
export default function CashMemberTable({
  rows = [],
  footerTerms = null,
  footerLabel = 'Total — organisation',
  personHeader = 'Doctor',
  showRole = false,
  showKindColumns = true,
  selectedKey = null,
  isRowClickable,
  onRowSelect,
  searchPlaceholder = 'Search…',
  resetKey = null,
}) {
  const [query, setQuery] = useState('');
  useEffect(() => {
    setQuery('');
  }, [resetKey]);
  const q = query.trim().toLowerCase();
  const shownRows = q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows;

  return (
    <div className="cmt rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-3 p-3 pb-2">
        <input
          type="text"
          data-testid="cmt-search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-xs flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        />
        {q && (
          <span data-testid="cmt-search-count" className="text-xs text-slate-500 dark:text-slate-400">
            {shownRows.length} of {rows.length} rows match
          </span>
        )}
      </div>

      <div className="cmt-scroll mx-3 overflow-auto rounded-lg border-t border-slate-200 dark:border-slate-700">
        <table className="cmt-table w-full text-left text-sm">
          <thead>
            <tr className="cmt-head text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <th className="px-3 py-2">{personHeader}</th>
              {showKindColumns && <th className="px-1.5 py-2 text-right tabular-nums">Consultation</th>}
              {showKindColumns && <th className="px-1.5 py-2 text-right tabular-nums">Procedures</th>}
              {showKindColumns && <th className="px-1.5 py-2 text-right tabular-nums">Referral</th>}
              <th className="px-1.5 py-2 text-right tabular-nums">Gross</th>
              <th className="px-1.5 py-2 text-right tabular-nums">Discounts</th>
              <th className="px-1.5 py-2 text-right tabular-nums">Refunds</th>
              <th className="cmt-col-sticky px-1.5 py-2 text-right tabular-nums" data-testid="cmt-net-header">Net collected</th>
            </tr>
          </thead>
          <tbody>
            {shownRows.map((row) => {
              const clickable = !!isRowClickable?.(row);
              const isSelected = selectedKey === row.key;
              return (
                <tr
                  key={row.key}
                  data-testid={`cmt-row-${row.key}`}
                  aria-selected={isSelected}
                  onClick={clickable ? () => onRowSelect?.(isSelected ? null : row.key) : undefined}
                  className={`cmt-row border-t border-slate-100 dark:border-slate-800 ${clickable ? 'cursor-pointer' : ''} ${isSelected ? 'cmt-row-selected' : ''}`}
                >
                  <td className="px-3 py-2">
                    <div className="cmt-name-cell">
                      <span className="cmt-name-txt" title={row.label}>{row.label}</span>
                      {showRole && row.role && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {STAFF_ROLE_LABELS[row.role] || row.role}
                        </span>
                      )}
                      {row.isNegative && (
                        <span
                          data-testid={`cmt-refund-tag-${row.key}`}
                          className="rounded border border-rose-400 bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 dark:border-rose-600 dark:bg-rose-900/40 dark:text-rose-200"
                        >
                          Refunds exceeded collections this month
                        </span>
                      )}
                    </div>
                  </td>
                  {showKindColumns && <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{row.consultation}</td>}
                  {showKindColumns && <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{row.procedures}</td>}
                  {showKindColumns && <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{row.referral}</td>}
                  <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{row.gross}</td>
                  <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{ded(row.discountsPaise)}</td>
                  <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{ded(row.refundsPaise)}</td>
                  <td
                    data-testid={`cmt-net-${row.key}`}
                    className={`cmt-col-sticky whitespace-nowrap px-1.5 py-2 text-right font-semibold tabular-nums ${row.isNegative ? 'text-rose-700 dark:text-rose-300' : ''}`}
                  >
                    {row.net}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {footerTerms && (
            <tfoot>
              <tr data-testid="cmt-footer" className="cmt-foot font-semibold text-slate-900 dark:text-slate-50">
                <td className="px-3 py-2">{footerLabel}</td>
                {showKindColumns && <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{footerTerms.consultation}</td>}
                {showKindColumns && <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{footerTerms.procedures}</td>}
                {showKindColumns && <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{footerTerms.referral}</td>}
                <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{footerTerms.gross}</td>
                <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{ded(footerTerms.discountsPaise)}</td>
                <td className="whitespace-nowrap px-1.5 py-2 text-right tabular-nums">{ded(footerTerms.refundsPaise)}</td>
                <td
                  data-testid="cmt-net-footer"
                  className={`cmt-col-sticky whitespace-nowrap px-1.5 py-2 text-right tabular-nums ${footerTerms.netPaise < 0 ? 'text-rose-700 dark:text-rose-300' : ''}`}
                >
                  {footerTerms.net}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p data-testid="cmt-count" className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
        {CASH_TAB_COPY.rowCountLine(rows.length)}
      </p>
    </div>
  );
}
